"use client";

import { useCallback, type Dispatch, type SetStateAction } from "react";

import type { PlaceResult } from "@/lib/api/misc";
import { isValidOptionalEmail } from "@/lib/phone-e164";
import { isContactGateSatisfied } from "./KundaliFormCard.validation";
import { KUNDALI_PHONE_CAPTURE_POLICY } from "@/lib/phone-capture-policy";
import { EmailField, PhoneField } from "./KundaliFormCard.contact-fields";
import {
  BirthDateField,
  BirthPlaceField,
  BirthTimeField,
  NameField,
} from "./KundaliFormCard.fields";
import type { KundaliLang, KundaliLocale, KundaliTranslations } from "./KundaliFormCard.i18n";
import {
  birthDateInlineError,
  normalizeBirthDateInput,
  type KundaliFieldErrors,
} from "./KundaliFormCard.validation";
import type { FieldFunnel } from "./useFieldFunnel";
import type { KundaliField, KundaliStepAction } from "@/lib/analytics-kundali";
import type {
  KundaliProgressiveCopy,
  KundaliProgressiveStep as Step,
} from "./KundaliFormCard.progressive";
import { progressiveStepForField } from "./KundaliFormCard.progressive";
import styles from "./KundaliFormCard.progressive.module.css";

type Props = {
  language: KundaliLang;
  /** What the fields' hints and inline errors are written in: a regional edition, else `language`. */
  locale?: KundaliLocale;
  t: KundaliTranslations;
  progressiveCopy?: KundaliProgressiveCopy;
  progressiveStep: Step;
  setProgressiveStep: Dispatch<SetStateAction<Step>>;
  name: string;
  setName: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  selectedPlace: PlaceResult | null;
  onClearPlace: () => void;
  ambiguousSuggestion: PlaceResult | null;
  fieldFunnel: FieldFunnel;
  markFormStarted: (field: string) => void;
  fieldErrors: KundaliFieldErrors;
  onProgressiveAction: (action: KundaliStepAction, step: Step) => void;
};

export function KundaliFormFields(props: Props) {
  const { progressiveCopy, progressiveStep, language } = props;
  if (!progressiveCopy) {
    return (
      <>
        <IdentityFields {...props} />
        <ChartFields {...props} />
        <ContactFields {...props} emailToggle={props.t.emailToggle} />
      </>
    );
  }

  const copy = progressiveCopy[language];
  const stepCopy = copy.steps[progressiveStep];
  return (
    <section aria-label={stepCopy.title} className={`${styles.progressiveSection} space-y-5`}>
      <StepHeading step={progressiveStep} copy={copy} />
      {progressiveStep === 0 && <IdentityFields {...props} />}
      {progressiveStep === 1 && <ChartFields {...props} />}
      {progressiveStep === 2 && <ContactFields {...props} emailToggle={copy.emailToggle} />}
      <StepNavigation {...props} copy={copy} />
    </section>
  );
}

function StepHeading({ step, copy }: { step: Step; copy: KundaliProgressiveCopy[KundaliLang] }) {
  const text = copy.progressLabel
    .replace("{current}", String(step + 1))
    .replace("{total}", String(copy.steps.length));
  return (
    <header aria-live="polite">
      <ol className={styles.progressRail} aria-label={text}>
        {copy.steps.map((item, index) => (
          <li
            key={item.title}
            aria-current={index === step ? "step" : undefined}
            data-complete={index < step ? "true" : "false"}
          >
            <span className="sr-only">{item.title}</span>
          </li>
        ))}
      </ol>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-intent-brand">{text}</p>
      <h3 className="mt-1 text-[20px] font-bold text-[var(--ish-dark)]">
        {copy.steps[step].title}
      </h3>
      <p className="mt-1 text-caption leading-relaxed text-[var(--ish-text-muted)]">
        {copy.steps[step].body}
      </p>
    </header>
  );
}

function StepNavigation({
  progressiveStep,
  setProgressiveStep,
  name,
  birthDate,
  birthPlace,
  birthTime,
  requireBirthTime,
  language,
  locale,
  copy,
  onProgressiveAction,
}: Props & { copy: KundaliProgressiveCopy[KundaliLang] }) {
  const canContinue =
    progressiveStep === 0
      ? Boolean(name.trim() && normalizeBirthDateInput(birthDate)) &&
        !birthDateInlineError(birthDate, locale ?? language)
      : Boolean(birthPlace.trim() && (!requireBirthTime || birthTime.trim()));
  const move = (step: Step, action: KundaliStepAction) => {
    onProgressiveAction(action, progressiveStep);
    setProgressiveStep(step);
  };

  return (
    <div className={styles.stepNavigation}>
      {progressiveStep > 0 && (
        <button
          type="button"
          onClick={() => move((progressiveStep - 1) as Step, "back")}
          className={styles.backButton}
        >
          {copy.backLabel}
        </button>
      )}
      {progressiveStep < 2 && (
        <button
          type="button"
          disabled={!canContinue}
          onMouseDown={(event) => {
            // Keep the active input mounted through the button's click. Date
            // normalization and place-autocomplete outside-click handlers can
            // otherwise rerender this button between mousedown and click,
            // silently dropping the seeker's first tap.
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={() => move((progressiveStep + 1) as Step, "complete")}
          className={styles.nextButton}
        >
          {copy.steps[progressiveStep].nextLabel}
        </button>
      )}
    </div>
  );
}

function IdentityFields(props: Props) {
  const handlers = useIdentityFieldHandlers(props);
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <NameField
        value={props.name}
        onChange={handlers.changeName}
        onFocus={handlers.focusName}
        language={props.locale ?? props.language}
        t={props.t}
        error={props.fieldErrors.name}
      />
      <BirthDateField
        value={props.birthDate}
        onChange={handlers.changeBirthDate}
        onFocus={handlers.focusBirthDate}
        language={props.locale ?? props.language}
        t={props.t}
        error={props.fieldErrors.birthDate}
      />
    </div>
  );
}

function ChartFields(props: Props) {
  const handlers = useChartFieldHandlers(props);
  return (
    <>
      <BirthTimeField
        value={props.birthTime}
        onChange={handlers.changeBirthTime}
        onFocus={handlers.focusBirthTime}
        language={props.locale ?? props.language}
        t={props.t}
        required={props.requireBirthTime}
        error={props.fieldErrors.birthTime}
      />
      <BirthPlaceField
        value={props.birthPlace}
        onChange={handlers.changeBirthPlace}
        onSelect={handlers.selectBirthPlace}
        onFocus={handlers.focusBirthPlace}
        language={props.locale ?? props.language}
        t={props.t}
        error={props.fieldErrors.birthPlace}
        selectedPlace={props.selectedPlace}
        onClearPlace={props.onClearPlace}
        ambiguousSuggestion={props.ambiguousSuggestion}
      />
    </>
  );
}

function ContactFields(props: Props & { emailToggle: string }) {
  const handlers = useContactFieldHandlers(props);
  return (
    <>
      <PhoneField
        value={props.phone}
        onChange={handlers.changePhone}
        onFocus={handlers.focusPhone}
        t={props.t}
        required={KUNDALI_PHONE_CAPTURE_POLICY.required}
        error={props.fieldErrors.phone}
      />
      <details className="rounded-lg border border-[var(--ish-border)] px-3 py-2">
        <summary className="min-h-11 cursor-pointer py-3 text-body-small font-semibold text-[var(--ish-text-muted)]">
          {props.emailToggle}
        </summary>
        <div className="pb-2 pt-1">
          <EmailField
            value={props.email}
            onChange={handlers.changeEmail}
            onFocus={handlers.focusEmail}
            t={props.t}
            error={props.fieldErrors.email}
          />
        </div>
      </details>
    </>
  );
}

function useFieldFocus(props: Props) {
  const { progressiveCopy, setProgressiveStep, markFormStarted } = props;
  const start = props.fieldFunnel.start;
  return useCallback(
    (field: KundaliField) => {
      if (progressiveCopy) setProgressiveStep(progressiveStepForField(field));
      markFormStarted(field);
      start(field);
    },
    [progressiveCopy, setProgressiveStep, markFormStarted, start]
  );
}

function useIdentityFieldHandlers(props: Props) {
  const focus = useFieldFocus(props);
  const valid = props.fieldFunnel.valid;
  const { setName, setBirthDate } = props;
  const changeName = useCallback(
    (value: string) => {
      setName(value);
      if (value.trim()) valid("name");
    },
    [setName, valid]
  );
  const changeBirthDate = useCallback(
    (value: string) => {
      setBirthDate(value);
      if (normalizeBirthDateInput(value) && !birthDateInlineError(value, "en")) valid("birthDate");
    },
    [setBirthDate, valid]
  );
  const focusName = useCallback(() => focus("name"), [focus]);
  const focusBirthDate = useCallback(() => focus("birthDate"), [focus]);
  return { changeName, changeBirthDate, focusName, focusBirthDate };
}

function useChartFieldHandlers(props: Props) {
  const focus = useFieldFocus(props);
  const valid = props.fieldFunnel.valid;
  const { setBirthTime, setBirthPlace, setSelectedPlace } = props;
  const changeBirthTime = useCallback(
    (value: string) => {
      setBirthTime(value);
      if (value.trim()) valid("birthTime");
    },
    [setBirthTime, valid]
  );
  const changeBirthPlace = useCallback(
    (value: string) => {
      setBirthPlace(value);
      setSelectedPlace(null);
    },
    [setBirthPlace, setSelectedPlace]
  );
  const selectBirthPlace = useCallback(
    (place: PlaceResult) => {
      setBirthPlace(place.display);
      setSelectedPlace(place);
      valid("birthPlace");
    },
    [setBirthPlace, setSelectedPlace, valid]
  );
  const focusBirthTime = useCallback(() => focus("birthTime"), [focus]);
  const focusBirthPlace = useCallback(() => focus("birthPlace"), [focus]);
  return {
    changeBirthTime,
    changeBirthPlace,
    selectBirthPlace,
    focusBirthTime,
    focusBirthPlace,
  };
}

function useContactFieldHandlers(props: Props) {
  const focus = useFieldFocus(props);
  const valid = props.fieldFunnel.valid;
  const { setPhone, setEmail } = props;
  const changePhone = useCallback(
    (value: string) => {
      setPhone(value);
      if (value.trim() && isContactGateSatisfied(value)) valid("phone");
    },
    [setPhone, valid]
  );
  const changeEmail = useCallback(
    (value: string) => {
      setEmail(value);
      if (value.trim() && isValidOptionalEmail(value)) valid("email");
    },
    [setEmail, valid]
  );
  const focusPhone = useCallback(() => focus("phone"), [focus]);
  const focusEmail = useCallback(() => focus("email"), [focus]);
  return { changePhone, changeEmail, focusPhone, focusEmail };
}
